import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface LayoutContextType {
  leftSidebarCollapsed: boolean;
  setLeftSidebarCollapsed: (collapsed: boolean) => void;
  rightSidebarCollapsed: boolean;
  setRightSidebarCollapsed: (collapsed: boolean) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

interface LayoutProviderProps {
  children: ReactNode;
}

export function LayoutProvider({ children }: LayoutProviderProps) {
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState<boolean>(false);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState<boolean>(false);

  useEffect(() => {
    const savedLeftCollapsed = localStorage.getItem("leftSidebarCollapsed");
    if (savedLeftCollapsed === "true") {
      setLeftSidebarCollapsed(true);
    }

    const savedRightCollapsed = localStorage.getItem("rightSidebarCollapsed");
    if (savedRightCollapsed === "true") {
      setRightSidebarCollapsed(true);
    }
  }, []);

  const handleLeftSidebarToggle = useCallback((collapsed: boolean) => {
    setLeftSidebarCollapsed(collapsed);
    localStorage.setItem("leftSidebarCollapsed", collapsed.toString());
  }, []);

  const handleRightSidebarToggle = useCallback((collapsed: boolean) => {
    setRightSidebarCollapsed(collapsed);
    localStorage.setItem("rightSidebarCollapsed", collapsed.toString());
  }, []);

  const value = useMemo<LayoutContextType>(
    () => ({
      leftSidebarCollapsed,
      setLeftSidebarCollapsed: handleLeftSidebarToggle,
      rightSidebarCollapsed,
      setRightSidebarCollapsed: handleRightSidebarToggle,
    }),
    [leftSidebarCollapsed, handleLeftSidebarToggle, rightSidebarCollapsed, handleRightSidebarToggle]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayout(): LayoutContextType {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
}
